import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { RefCallback, useEffect, useMemo } from "react";
import { Input } from "@chakra-ui/react";
import createFormFieldComponents from "components/Shared/Forms/FormField";
import Form, { OnValidSubmitHandler } from "components/Shared/Forms/Form";
import { Writeable } from "utils/typescriptUtilis";
import { SkuBrandOrderingPlatformInput } from "__generated__/gql/graphql";
import * as skuBrandOrderingPlatformSchemaFields from "utils/schemas/skuBrandOrderingPlatform";
import {
  makePostgraphileErrorProcessor,
  getPostgraphileErrorMatchersForFields,
} from "utils/PostgraphileErrorExtensions";
import NumberInput from "components/Shared/Forms/NumberInput";
import SelectSkuMenu from "components/Shared/Select/SelectSkuMenu";
import { FocusableElement } from "components/Shared/DrawerForm";
import DistributionCenterSelect from "components/Shared/Select/DistributionCenterSelect";
import { getFragmentData, gql } from "__generated__/gql";
import { useBrandQuery } from "utils/apollo/hooks/brand";
import BrandOrderingPlatformSelect from "components/Shared/Select/BrandOrderingPlatformSelect";

export const skuBrandOrderingPlatformFields = [
  "skuId",
  "brandOrderingPlatformId",
  "itemCode",
  "wholesaleUnitPrice",
] as const;

export type SkuBrandOrderingPlatformFormFields = Required<
  Writeable<
    Pick<
      SkuBrandOrderingPlatformInput,
      typeof skuBrandOrderingPlatformFields[number]
    >
  >
> & {
  distributionCenterIds: UUID[];
};

const schema: yup.ObjectSchema<SkuBrandOrderingPlatformFormFields> = yup
  .object()
  .shape({
    skuId: skuBrandOrderingPlatformSchemaFields.skuId,
    brandOrderingPlatformId:
      skuBrandOrderingPlatformSchemaFields.brandOrderingPlatformId,
    itemCode: skuBrandOrderingPlatformSchemaFields.itemCode,
    wholesaleUnitPrice: skuBrandOrderingPlatformSchemaFields.wholesaleUnitPrice,
    distributionCenterIds:
      skuBrandOrderingPlatformSchemaFields.distributionCenterIds,
  });

const { ControlledFormField, FormField } =
  createFormFieldComponents<SkuBrandOrderingPlatformFormFields>({
    schema,
  });

export const processApolloError =
  makePostgraphileErrorProcessor<SkuBrandOrderingPlatformFormFields>(
    getPostgraphileErrorMatchersForFields<
      keyof SkuBrandOrderingPlatformFormFields
    >(skuBrandOrderingPlatformFields, {
      type: "SkuBrandOrderingPlatform",
    }),
  );

const orderingPlatformFragment = gql(/* GraphQL */ `
  fragment SkuOrderingPlatformsOrderingPlatformDetails on OrderingPlatform {
    id
    name
    distributionCenters(
      filter: { name: { isNull: false }, isArchived: { equalTo: false } }
    ) {
      totalCount
    }
  }
`);

const orderingPlatformQuery = gql(/* GraphQL */ `
  query SkuOrderingPlatformFieldElementDetails(
    $brandOrderingPlatformId: UUID!
  ) {
    brandOrderingPlatform(id: $brandOrderingPlatformId) {
      id
      orderingPlatform {
        id
        ...SkuOrderingPlatformsOrderingPlatformDetails
      }
    }
  }
`);

export type SkuBrandOrderingPlatformFormProps = {
  brandOrderingPlatformId?: UUID | null;
  defaultValues?: Partial<SkuBrandOrderingPlatformFormFields>;
  footer?: React.ReactNode;
  formId?: string;
  initialFocusRef?: RefCallback<FocusableElement>;
  isBrandOrderingPlatformHidden?: boolean;
  isLoaded?: boolean;
  isSkuHidden?: boolean;
  onValidSubmit: OnValidSubmitHandler<SkuBrandOrderingPlatformFormFields>;
  skuId?: UUID | null;
};

export default function SkuBrandOrderingPlatformForm({
  brandOrderingPlatformId: brandOrderingPlatformIdProp,
  defaultValues: defaultValuesProp,
  footer,
  formId = "sku-brand-customer-form",
  initialFocusRef,
  isBrandOrderingPlatformHidden,
  isLoaded,
  isSkuHidden,
  onValidSubmit,
  skuId,
}: SkuBrandOrderingPlatformFormProps) {
  const defaultValues = useMemo(() => {
    const fields = { ...defaultValuesProp };

    if (skuId) fields.skuId = skuId;

    if (brandOrderingPlatformIdProp) {
      fields.brandOrderingPlatformId = brandOrderingPlatformIdProp;
    }

    return fields;
  }, [defaultValuesProp, skuId, brandOrderingPlatformIdProp]);

  const formContext = useForm<SkuBrandOrderingPlatformFormFields>({
    resolver: yupResolver(schema),
    defaultValues,
  });

  const { reset } = formContext;

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const [brandOrderingPlatformId] = formContext.watch([
    "brandOrderingPlatformId",
  ]);

  const { data } = useBrandQuery(orderingPlatformQuery, {
    skip: !brandOrderingPlatformId,
    variables: brandOrderingPlatformId
      ? { brandOrderingPlatformId }
      : undefined,
  });

  const op = getFragmentData(
    orderingPlatformFragment,
    data?.brandOrderingPlatform?.orderingPlatform,
  );

  return (
    <Form {...{ formContext, onValidSubmit, isLoaded }} id={formId}>
      {!isSkuHidden && (
        <ControlledFormField
          name="skuId"
          isDisabled={!!skuId}
          renderInput={({ field: { ref, ...field } }) => (
            <SelectSkuMenu
              {...field}
              autoFocus
              isDisabled={!!skuId}
              ref={(c) => {
                ref(c);
                initialFocusRef?.(c);
              }}
            />
          )}
          mt={0}
        />
      )}

      {!isBrandOrderingPlatformHidden && (
        <ControlledFormField
          name="brandOrderingPlatformId"
          isDisabled={!!brandOrderingPlatformIdProp}
          renderInput={({ field: { ref, ...field } }) => (
            <BrandOrderingPlatformSelect
              {...field}
              autoFocus
              isDisabled={!!brandOrderingPlatformIdProp}
              ref={(c) => {
                ref(c);
                initialFocusRef?.(c);
              }}
            />
          )}
          mt={0}
        />
      )}

      <FormField
        name="itemCode"
        renderInput={(props) => (
          <Input
            {...props}
            fontFamily="mono"
            placeholder={`${op?.name || "This customer"} item code`}
          />
        )}
      />

      <ControlledFormField
        name="wholesaleUnitPrice"
        renderInput={({ field }) => (
          <NumberInput
            {...field}
            max={100000}
            decimalScale={2}
            fixedDecimalScale
            prefix="$"
            allowNegative={false}
            placeholder={`${op?.name || "This customer"} wholesale unit price`}
          />
        )}
      />

      {!!op?.distributionCenters?.totalCount && (
        <ControlledFormField
          name="distributionCenterIds"
          isRequired={false}
          renderInput={({ field }) => (
            <DistributionCenterSelect
              isMulti
              {...field}
              orderingPlatformId={op.id}
              filter={{ name: { isNull: false } }}
              menuPortalTarget={document.body}
              menuPlacement="auto"
            />
          )}
          mb={0}
        />
      )}

      {footer}
    </Form>
  );
}
